import {Component} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {BiometryVendor, LivenessQrApiService} from '@npck/auth-frontend-api';
import {OauthRedirectErrorCode} from '@npck/errors-api';
import {AnalyticsService} from '@npck/libs/analytics-sdk';
import {RouterService} from '@npck/libs/commons';
import {AuthHolderService} from '../../../domain/auth-holder.service';
import {OauthService} from '../../../domain/oauth.service';
import {QR_ID} from '../../../domain/query-param.keys';
import {
    AUTH_VIDEO_IDENTIFICATION_BLOCKED,
    AUTH_VIDEO_IDENTIFICATION_FAILED,
    AUTH_VIDEO_IDENTIFICATION_RETRY,
    AUTH_VIDEO_IDENTIFICATION_SUCCEED,
} from '../../../infra/analytics-events';
import {Error} from '../../../infra/app-error-handler.service';
import {Path} from '../../../path';
import {LivenessServiceResponse, LivenessServiceStatus} from '../../liveness-device/liveness-device.component';

@Component({
    templateUrl: 'liveness-qr.component.html',
    standalone: false,
})
export class LivenessQrComponent {
    qrId: string;
    error: any;
    retryStatus: LivenessServiceStatus;
    retryIconName: string;
    buttonType: 'primary' | 'secondary';
    openPage = Pages.LIVENESS;
    Pages = Pages;
    biometryVendor: BiometryVendor = this.authHolderService.biometryVendor;

    constructor(
        private livenessQrApiService: LivenessQrApiService,
        private activatedRoute: ActivatedRoute,
        private oauthService: OauthService,
        private routerService: RouterService,
        private authHolderService: AuthHolderService,
        private analyticsService: AnalyticsService,
    ) {
        this.qrId = this.activatedRoute.snapshot.paramMap.get(QR_ID);
    }

    handleLivenessStatus(livenessStatusEvent: LivenessServiceResponse): void {
        switch (livenessStatusEvent.status) {
            case LivenessServiceStatus.SUCCEED: {
                this.logEvent(AUTH_VIDEO_IDENTIFICATION_SUCCEED, livenessStatusEvent);
                this.livenessQrApiService.updateQrStatus(this.qrId)
                    .subscribe(() => {
                        setTimeout(() => this.routerService.navigate(`${Path.LIVENESS_QR_SUCCEEDED}/${this.qrId}`), 3000);
                    });
                break;
            }
            case LivenessServiceStatus.LIMIT_PER_SESSION_EXCEEDED: {
                this.logEvent(AUTH_VIDEO_IDENTIFICATION_BLOCKED, livenessStatusEvent);
                this.oauthService.handleOauthError(OauthRedirectErrorCode.liveness_attempts_exceeded);
                break;
            }
            case LivenessServiceStatus.FACE_NOT_MATCHED: {
                this.logEvent(AUTH_VIDEO_IDENTIFICATION_FAILED, livenessStatusEvent);
                this.livenessQrApiService.updateQrStatus(this.qrId)
                    .subscribe(() => {
                        this.openPage = Pages.ERROR;
                        this.error = OauthRedirectErrorCode.gbdfl_face_not_matched;
                    });
                break;
            }
            case LivenessServiceStatus.NETWORK_ERROR:
            case LivenessServiceStatus.LIVENESS_ERROR:
            case LivenessServiceStatus.DEVICE_NOT_SUPPORTED:
                this.logEvent(AUTH_VIDEO_IDENTIFICATION_FAILED, livenessStatusEvent);
                this.livenessQrApiService.failLivenessQrCamera(this.qrId)
                    .subscribe(() => {
                        this.oauthService.handleOauthError(Error.DEVICE_NOT_SUPPORTED);
                    });
                break;
            case LivenessServiceStatus.CAMERA_FAILED: {
                this.logEvent(AUTH_VIDEO_IDENTIFICATION_FAILED, livenessStatusEvent);
                this.livenessQrApiService.failLivenessQrCamera(this.qrId)
                    .subscribe(() => {
                        this.oauthService.handleOauthError(Error.CAMERA_UNAVAILABLE);
                    });
                break;
            }
            case LivenessServiceStatus.NETWORK_PROBLEM: {
                this.logEvent(AUTH_VIDEO_IDENTIFICATION_RETRY, livenessStatusEvent);
                this.openPage = Pages.RETRY;
                this.retryStatus = livenessStatusEvent.status;
                this.buttonType = 'secondary';
                this.retryIconName = 'ic_network_warning';
                break;
            }
            case LivenessServiceStatus.LIVENESS_FAILED:
            case LivenessServiceStatus.CANCEL: {
                this.logEvent(AUTH_VIDEO_IDENTIFICATION_FAILED, livenessStatusEvent);
                this.openPage = Pages.RETRY;
                this.buttonType = 'primary';
                this.retryStatus = livenessStatusEvent.status;
                this.retryIconName = 'ic_face_scan';
                return;
            }
            case LivenessServiceStatus.ORIENTATION_ERROR: {
                this.logEvent(AUTH_VIDEO_IDENTIFICATION_FAILED, livenessStatusEvent);
                this.openPage = Pages.RETRY;
                this.buttonType = 'secondary';
                this.retryStatus = livenessStatusEvent.status;
                this.retryIconName = 'ic_orientation_error';
                break;
            }
        }
    }

    retry(): void {
        this.openPage = Pages.LIVENESS;
    }

    private logEvent(eventName: string, livenessStatusEvent: LivenessServiceResponse, additionalProperties: {
        [key: string]: string
    } = {}): void {
        this.analyticsService.capture(eventName, {
            biometry_vendor: this.biometryVendor,
            reason: livenessStatusEvent.status,
            detailed_reason: livenessStatusEvent.rootStatus,
            qr_id: this.qrId,
            qr_bool: true,
            ...additionalProperties,
        });
    }
}

export enum Pages {
    ERROR = 'ERROR',
    LIVENESS = 'LIVENESS',
    RETRY = 'RETRY'
}
